import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ConversationsList } from './components/ConversationsList';
import { MessagesView } from './components/MessagesView';
import { Header } from './components/Header';
import { AuthProvider, useAuth } from './provider/AuthContext';
import Callback from './pages/Callback/Callback';
import { Conversation, Message } from './types';
import AuthService from './services/AuthService';


const AppContent: React.FC = () => {
  const { token, phoneNumber, isAuthenticated } = useAuth();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const { phone: urlPhone } = useParams<{ phone?: string }>();
  const navigate = useNavigate();
  
  const [isConnected, setIsConnected] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPhone, setUserPhoneState] = useState<string>(phoneNumber);
  const [availablePhones, setAvailablePhones] = useState<string[]>([]);
  const [phoneData, setPhoneData] = useState<{phone: string, name?: string}[]>([]);
  const [activePhoneFilter, setActivePhoneFilter] = useState<string | undefined>(urlPhone);
  const [selectedPhone, setSelectedPhone] = useState<string>('');
  const [instancePhones, setInstancePhones] = useState<string[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  useEffect(() => {
    if (phoneNumber) {
      setUserPhoneState(phoneNumber);
    }
  }, [phoneNumber]);

  useEffect(() => {
    const getCookie = (name: string) => {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === name) return value;
      }
      return null;
    };

    if (urlPhone) {
      document.cookie = `targetPhone=${urlPhone}; path=/; max-age=3600`;
    } else if (isAuthenticated) {
      const targetPhone = getCookie('targetPhone');
      if (targetPhone) {
        navigate(`/phone/${targetPhone}`);
      }
    }
  }, [urlPhone, isAuthenticated, navigate]);
  
  useEffect(() => {
    const checkAuth = () => {
      const cookieToken = document.cookie
        .split(';')
        .find(cookie => cookie.trim().startsWith('token='))
        ?.split('=')[1];
      
      const hasToken = cookieToken && cookieToken.trim() !== '';
      const hasAuthCookies = document.cookie.includes('token') || document.cookie.includes('user');
      
      if ((hasToken && hasAuthCookies) || (token && token.trim() !== '')) {
        return;
      } else {
        document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        document.cookie = 'user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        
        localStorage.clear();
        
        AuthService.getStartUrl().then(startUrl => {
          window.location.href = startUrl.split('?')[0];
        });
      }
    };
    
    const timer = setTimeout(checkAuth, 1000);
    
    return () => clearTimeout(timer);
  }, [isAuthenticated, token, urlPhone]);

  const loadAvailablePhones = useCallback(async (searchTerm: string = ''): Promise<{ phone: string, name?: string }[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    try {
      const params = new URLSearchParams();
      params.append('search', searchTerm);
      params.append('limit', '50');

      const phonesResponse = await fetch(`${baseUrl}/api/phones-simple?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (phonesResponse.ok) {
        const phonesData = await phonesResponse.json();
        setPhoneData(phonesData);
        setAvailablePhones(phonesData.map((phone: any) => phone.phone));
        return phonesData;
      } else {
        console.error('❌ Failed to load phones:', phonesResponse.status);
        const errorText = await phonesResponse.text();
        console.error('❌ Error details:', errorText);
        return [];
      }
    } catch (err) {
      console.error('❌ Error loading available phones:', err);
      return [];
    }
  }, [token, baseUrl]);

  const loadInstancePhones = useCallback(async () => {
    if (instancePhones.length > 0) {
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/api/instances/phones`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const phones = await response.json();
        setInstancePhones(phones);
      } else {
        console.error('❌ Failed to load instance phones:', response.status);
      }
    } catch (err) {
      console.error('❌ Error loading instance phones:', err);
    }
  }, [token, baseUrl, instancePhones.length]);

  const loadConversations = useCallback(async (silent: boolean = false, filterPhone?: string) => {
    try {
      if (!silent) setLoading(true);
      
      setSelectedConversation(null);
      setMessages([]);
      if (!filterPhone || filterPhone === '') {
        const response = await fetch(`${baseUrl}/api/conversations/allowed`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ Failed to load conversations:', errorData);
          throw new Error(errorData.error || 'Failed to load conversations');
        }

        const data = await response.json();
        
        setConversations(prevConversations => {
          if (JSON.stringify(prevConversations) !== JSON.stringify(data)) {
            return data;
          }
          return prevConversations;
        });
      } else {
        const response = await fetch(`${baseUrl}/api/conversations/user/${encodeURIComponent(filterPhone)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
          }
          console.error('❌ Failed to load conversations:', errorMessage);
          throw new Error(errorMessage);
        }

        const data = await response.json();
        
        setConversations(prevConversations => {
          if (JSON.stringify(prevConversations) !== JSON.stringify(data)) {
            return data;
          }
          return prevConversations;
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('❌ Error in loadConversations:', err);
      setSelectedConversation(null);
      setMessages([]);
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load conversations');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, baseUrl]);

  const handleDatabaseConnect = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${baseUrl}/api/connect-database`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect to database');
      }

      setIsConnected(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  }, [token, baseUrl]);

  const loadMessages = useCallback(async (conversationId: string, silent: boolean = false, append: boolean = false) => {
    try {
      if (!silent && !append) {
        setLoading(true);
        setError(null);
      }
      
      const offset = append ? messages.length : 0;
      const url = `${baseUrl}/api/messages/${encodeURIComponent(conversationId)}?limit=100&offset=${offset}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Failed to load messages:', errorData);
        throw new Error(errorData.error || 'Failed to load messages');
      }

      const data = await response.json();
      
      if (data.length < 100) {
        setHasMoreMessages(false);
      }
      
      setMessages(prevMessages => {
        if (append) {
          return [...data, ...prevMessages];
        }
        return data;
      });
      
      if (!silent) setError(null);
    } catch (err) {
      console.error('❌ Error loading messages:', err);
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      }
    } finally {
      if (!silent && !append) setLoading(false);
      if (append) setLoadingMore(false);
    }
  }, [token, messages.length, baseUrl]);

  const loadMoreMessages = useCallback(async (conversationId: string) => {
    if (loadingMore || !hasMoreMessages) {
      return;
    }
    
    setLoadingMore(true);
    await loadMessages(conversationId, true, true);
  }, [loadMessages, loadingMore, hasMoreMessages]);

  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversation(conversationId);
    setMessages([]);
    setHasMoreMessages(true);
    loadInstancePhones();
    loadMessages(conversationId);
  };

  const handleUpdateUserPhone = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '');
    setUserPhoneState(cleanPhone);
  };

  const handlePhoneSelected = (phone: string) => {
    setSelectedPhone(phone);
    setActivePhoneFilter(phone);
  };

  useEffect(() => {
    const checkBackendConnection = async () => {
      if (!isConnected) {
        try {
          const response = await fetch(`${baseUrl}/api/health`);
          
          if (response.ok) {
            const data = await response.json();
            
            if (data.status === 'ok') {
              if (data.connected) {
                setIsConnected(true);
                setError('');
              } else if (data.connecting) {
                setError('Conectando ao banco de dados...');
              } else {
                setError('Banco de dados não conectado');
              }
            }
          }
        } catch (error) {
          console.error('❌ Failed to check backend connection:', error);
          setError('Falha ao conectar com o servidor');
        }
      }
    };

    const timer = setTimeout(checkBackendConnection, 1000);
    return () => clearTimeout(timer);
  }, [isConnected, baseUrl]);

  useEffect(() => {
    if (urlPhone && isConnected) {
      setActivePhoneFilter(urlPhone);
      loadConversations(false, urlPhone);
    }
  }, [urlPhone, isConnected, loadConversations]);


  return (
    <div className="app">
      <div className="sidebar">
        <Header isAutoRefreshActive={false} />
        
        {!isConnected && (
          <div className="loading-fullscreen">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <div className="loading-text">Carregando...</div>
              </div>
            ) : error ? (
              <div className="error">
                ❌ Falha na conexão
                Se o erro persistir, entre em contato com o suporte.
              </div>
            ) : (
              <div className="loading-container">
                <div className="spinner"></div>
                <div className="loading-text">Carregando...</div>
              </div>
            )}
          </div>
        )}

        {isConnected && (
          <ConversationsList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleConversationSelect}
            loading={loading}
            userPhone={activePhoneFilter || selectedPhone || userPhone}
            onLoadConversations={loadConversations}
            onPhoneSelected={handlePhoneSelected}
            onSearchPhones={loadAvailablePhones}
            availablePhones={availablePhones}
            phoneData={phoneData}
            urlPhone={urlPhone}
          />
        )}
      </div>

      <div className="main-content">
        <MessagesView
          messages={messages}
          selectedConversation={selectedConversation}
          selectedConversationData={conversations.find(c => c.chat_id === selectedConversation) || null}
          loading={loading}
          userPhone={activePhoneFilter || selectedPhone || userPhone}
          onUpdateUserPhone={handleUpdateUserPhone}
          onRefreshMessages={() => selectedConversation && loadMessages(selectedConversation)}
          instancePhones={instancePhones}
          onLoadMoreMessages={() => selectedConversation && loadMoreMessages(selectedConversation)}
          hasMoreMessages={hasMoreMessages}
          loadingMore={loadingMore}
        />
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/phone/:phone" element={<AppContent />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
